import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useBridgeClubAuth } from '../../contexts/BridgeClubAuthContext';
import { bridgeClubAPI } from '../../services/bridgeClubAPI';
import { universalLogin } from '../../services/authService';
import InstallAppBanner from '../../components/InstallAppBanner';
import PasswordInput from '../../components/common/PasswordInput';
import './BridgeClubLogin.css';

const BridgeClubLogin: React.FC = () => {
  const { isAuthenticated, isLoading, error, clearError } = useBridgeClubAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get redirect URL from query params (e.g., ?redirect=/bridge-club/feed)
  const redirectPath = searchParams.get('redirect') || '/bridge-club/dashboard';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [resendingEmail, setResendingEmail] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);

  // Multi-account selector state
  const [showAccountSelector, setShowAccountSelector] = useState(false);
  const [availableAccounts, setAvailableAccounts] = useState<any[]>([]);
  const [savedCredentials, setSavedCredentials] = useState<{ email: string; password: string } | null>(null);

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState<string | null>(null);

  // Standalone resend verification state
  const [showResendForm, setShowResendForm] = useState(false);
  const [resendFormEmail, setResendFormEmail] = useState('');
  const [resendFormLoading, setResendFormLoading] = useState(false);
  const [resendFormSuccess, setResendFormSuccess] = useState(false);
  const [resendFormError, setResendFormError] = useState<string | null>(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  useEffect(() => {
    if (isAuthenticated) {
      // Redirect to the requested path, or dashboard if none specified
      navigate(redirectPath);
    }
  }, [isAuthenticated, navigate, redirectPath]);

  // Track QR code visits - send analytics when user lands on login page
  useEffect(() => {
    const storeCode = searchParams.get('store');
    const placement = searchParams.get('placement');

    // Only track if this looks like a QR code visit
    if (storeCode || placement) {
      fetch(`${API_BASE_URL}/bridge-club/track-visit/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          store_code: storeCode || '',
          placement: placement || 'direct',
          portal: 'bridge_club',
          path: '/bridge-club/login',
        }),
      }).catch(() => {}); // Silently ignore errors
    }
  }, [searchParams, API_BASE_URL]);

  useEffect(() => {
    clearError();
    setLocalError(null);
    setNeedsVerification(false);
  }, [email, password, clearError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setLocalError(null);
    setNeedsVerification(false);

    try {
      // Use universal login - works for any user type
      const result = await universalLogin(email, password);

      if (result.success) {
        // Check if multiple accounts found
        if (result.auto_login === false && result.accounts && result.accounts.length > 1) {
          // Show account selector
          setAvailableAccounts(result.accounts);
          setSavedCredentials({ email, password });
          setShowAccountSelector(true);
          setSubmitting(false);
          return;
        }

        // Single account or auto-login - redirect to the appropriate portal
        if (result.redirect_url) {
          window.location.href = result.redirect_url;
        } else {
          // Fallback to the requested path
          window.location.href = redirectPath;
        }
      } else if (result.requires_verification) {
        // Bridge Club member needs email verification
        setNeedsVerification(true);
        setVerificationEmail(email);
      } else {
        setLocalError(result.error || 'Invalid email or password');
      }
    } catch (err: any) {
      // Check if this is a verification required error
      if (err?.requires_verification) {
        setNeedsVerification(true);
        setVerificationEmail(err?.email || email);
      } else {
        setLocalError(err?.error || 'Login failed. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendVerification = async () => {
    setResendingEmail(true);
    setResendError(null);
    setResendSuccess(false);

    try {
      await bridgeClubAPI.resendVerification(verificationEmail);
      setResendSuccess(true);
    } catch (err: any) {
      setResendError(err?.error || 'Failed to resend verification email. Please try again.');
    } finally {
      setResendingEmail(false);
    }
  };

  const handleBackToLogin = () => {
    setNeedsVerification(false);
    setResendSuccess(false);
    setResendError(null);
    setPassword('');
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotPasswordError(null);
    setForgotPasswordLoading(true);

    try {
      await bridgeClubAPI.forgotPassword(forgotPasswordEmail);
      setForgotPasswordSuccess(true);
    } catch (err: any) {
      setForgotPasswordError(err?.error || 'Failed to send reset email. Please try again.');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleResendFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setResendFormError(null);
    setResendFormLoading(true);

    try {
      await bridgeClubAPI.resendVerification(resendFormEmail);
      setResendFormSuccess(true);
    } catch (err: any) {
      setResendFormError(err?.error || 'Failed to send verification email. Please try again.');
    } finally {
      setResendFormLoading(false);
    }
  };

  const handleAccountSelect = async (account: any) => {
    if (!savedCredentials) return;

    setSubmitting(true);
    setLocalError(null);

    try {
      // Call universal login with the selected account type and portal_user_id
      const body: any = {
        email: savedCredentials.email.toLowerCase().trim(),
        password: savedCredentials.password,
        account_type: account.type,
      };

      // For portal accounts, include portal_user_id to identify which one
      if (account.portal_user_id) {
        body.portal_user_id = account.portal_user_id;
      }

      const response = await fetch(`${API_BASE_URL}/auth/universal-login/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Store user data based on account type
        if (data.user_type === 'portal') {
          localStorage.setItem('portal_user', JSON.stringify(data.user));
        } else if (data.user_type === 'bridge_club') {
          localStorage.setItem('bridge_club_member', JSON.stringify(data.member));
        }

        // Redirect
        if (data.redirect_url) {
          window.location.href = data.redirect_url;
        } else if (account.type === 'bridge_club') {
          window.location.href = '/bridge-club/dashboard';
        } else {
          window.location.href = '/portal/dashboard';
        }
      } else {
        setLocalError(data.error || 'Login failed. Please try again.');
        setShowAccountSelector(false);
      }
    } catch (err: any) {
      setLocalError('Network error. Please try again.');
      setShowAccountSelector(false);
    } finally {
      setSubmitting(false);
    }
  };

  // Track website link clicks for analytics
  const handleWebsiteLinkClick = () => {
    try {
      fetch(`${API_BASE_URL}/bridge-club/track-visit/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          portal: 'bridge_club',
          placement: 'website',
          path: '/external/website',
        }),
      }).catch(() => {}); // Silently ignore errors - don't block navigation
    } catch (e) {
      // Ignore errors
    }
  };

  if (isLoading) {
    return (
      <div className="bridge-club-login-page">
        <div className="login-loading">
          <div className="login-spinner"></div>
        </div>
      </div>
    );
  }

  // Show verification required screen
  if (needsVerification) {
    return (
      <div className="bridge-club-login-page">
        <div className="login-container">
          <div className="login-header">
            <img src="/rbf-logo.png" alt="Red Bridge Farm Feed Co." className="login-logo" />
            <h1 className="login-title">Verify Your Email</h1>
          </div>

          <div className="verification-required">
            <div className="verification-icon">📧</div>
            <p className="verification-message">
              Please verify your email address to continue.
            </p>
            <p className="verification-email">{verificationEmail}</p>

            {resendSuccess ? (
              <div className="verification-success">
                <span className="success-icon">✓</span>
                <p>Verification email sent! Please check your inbox.</p>
                <p className="check-spam">Don't forget to check your spam folder.</p>
              </div>
            ) : (
              <>
                {resendError && <div className="resend-error">{resendError}</div>}
                <button
                  onClick={handleResendVerification}
                  disabled={resendingEmail}
                  className="resend-button"
                >
                  {resendingEmail ? 'Sending...' : 'Resend Verification Email'}
                </button>
              </>
            )}

            <button onClick={handleBackToLogin} className="back-to-login">
              ← Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bridge-club-login-page">
      <div className="login-container">
        <div className="login-header">
          <img src="/rbf-logo.png" alt="Red Bridge Farm Feed Co." className="login-logo" />
          <h1 className="login-title">Bridge Club</h1>
          <p className="login-tagline">Smarter Feeding Starts Here.</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
              autoComplete="email"
              style={{
                backgroundColor: '#ffffff',
                color: '#1f2937',
                border: '2px solid #9ca3af',
                padding: '14px 16px',
                borderRadius: '8px',
                width: '100%',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <PasswordInput
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              autoComplete="current-password"
              style={{
                backgroundColor: '#ffffff',
                color: '#1f2937',
                border: '2px solid #9ca3af',
                padding: '14px 16px',
                borderRadius: '8px',
                width: '100%',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {(error || localError) && !needsVerification && <div className="login-error">{localError || error}</div>}

          <button type="submit" className="login-button" disabled={submitting}>
            {submitting ? 'Signing in...' : 'Sign In'}
          </button>

          <div style={{ textAlign: 'center', marginTop: '1rem' }}>
            <button
              type="button"
              onClick={() => {
                setShowForgotPassword(true);
                setForgotPasswordEmail(email);
                setForgotPasswordError(null);
                setForgotPasswordSuccess(false);
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#6b7280',
                fontSize: '0.875rem',
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              Forgot your password?
            </button>
            <br />
            <button
              type="button"
              onClick={() => {
                setShowResendForm(true);
                setResendFormEmail(email);
                setResendFormError(null);
                setResendFormSuccess(false);
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#6b7280',
                fontSize: '0.875rem',
                cursor: 'pointer',
                textDecoration: 'underline',
                marginTop: '0.5rem',
              }}
            >
              Didn't get your verification email?
            </button>
          </div>
        </form>

        <div className="login-footer">
          <p>Don't have an account?</p>
          <Link to="/bridge-club" className="signup-link">Join Bridge Club</Link>
        </div>

        <div className="learn-more-section">
          <p>Want to learn more about Red Bridge Farm Feed Co.?</p>
          <a
            href="https://www.redbridgefeed.com?src=bridgeclub"
            target="_blank"
            rel="noopener noreferrer"
            className="website-link"
            onClick={handleWebsiteLinkClick}
          >
            🌾 Visit Our Website
          </a>
        </div>

        <div className="login-benefits">
          <h3>Member Benefits</h3>
          <ul>
            <li>🎁 Buy 10 bags, get 11th FREE!</li>
            <li>📱 Track your animals & feed</li>
            <li>🔔 Get feed reminders</li>
            <li>🌤️ Weather alerts for your animals</li>
            <li>🏆 Earn rewards & help improve feed for everyone</li>
          </ul>
        </div>
      </div>

      {/* Account Selector Modal */}
      {showAccountSelector && (
        <div className="account-selector-overlay" onClick={() => setShowAccountSelector(false)}>
          <div className="account-selector-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="account-selector-close"
              onClick={() => setShowAccountSelector(false)}
              aria-label="Close"
            >
              &times;
            </button>

            <h2 className="account-selector-title">Choose Your Account</h2>
            <p className="account-selector-subtitle">
              Multiple accounts found for <strong>{savedCredentials?.email}</strong>. Which would you like to access?
            </p>

            <div className="account-selector-list">
              {availableAccounts.map((account, index) => (
                <button
                  key={index}
                  className="account-selector-option"
                  onClick={() => handleAccountSelect(account)}
                  disabled={submitting}
                >
                  <span className="account-selector-icon">
                    {account.icon || (account.type === 'bridge_club' ? '🌾' : account.type === 'portal' ? '🏪' : '👤')}
                  </span>
                  <div className="account-selector-info">
                    <div className="account-selector-label">{account.label}</div>
                    <div className="account-selector-desc">{account.subtitle}</div>
                  </div>
                  <span className="account-selector-arrow">→</span>
                </button>
              ))}
            </div>

            {submitting && (
              <p className="account-selector-loading">Signing in...</p>
            )}

            {localError && <div className="account-selector-error">{localError}</div>}
          </div>
        </div>
      )}

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="account-selector-overlay" onClick={() => setShowForgotPassword(false)}>
          <div className="account-selector-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="account-selector-close"
              onClick={() => setShowForgotPassword(false)}
              aria-label="Close"
            >
              &times;
            </button>

            {forgotPasswordSuccess ? (
              <div style={{ textAlign: 'center' }}>
                <div className="verification-icon">&#10003;</div>
                <h2 className="account-selector-title">Check Your Email</h2>
                <p className="account-selector-subtitle">
                  If an account exists with <strong>{forgotPasswordEmail}</strong>, we've sent password reset instructions.
                </p>
                <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1.5rem' }}>
                  The link will expire in 24 hours. Check your spam folder if you don't see it.
                </p>
                <button
                  className="login-button"
                  onClick={() => setShowForgotPassword(false)}
                >
                  Back to Sign In
                </button>
              </div>
            ) : (
              <>
                <h2 className="account-selector-title">Reset Your Password</h2>
                <p className="account-selector-subtitle">
                  Enter your email address and we'll send you instructions to reset your password.
                </p>

                <form onSubmit={handleForgotPassword}>
                  {forgotPasswordError && (
                    <div className="login-error">{forgotPasswordError}</div>
                  )}

                  <div className="form-group">
                    <label htmlFor="forgotEmail">Email Address</label>
                    <input
                      type="email"
                      id="forgotEmail"
                      value={forgotPasswordEmail}
                      onChange={(e) => setForgotPasswordEmail(e.target.value)}
                      placeholder="your.email@example.com"
                      required
                      disabled={forgotPasswordLoading}
                      autoFocus
                    />
                  </div>

                  <button
                    type="submit"
                    className="login-button"
                    disabled={forgotPasswordLoading}
                    style={{ marginTop: '0.5rem' }}
                  >
                    {forgotPasswordLoading ? 'Sending...' : 'Send Reset Instructions'}
                  </button>
                </form>

                <p style={{ textAlign: 'center', marginTop: '1rem', fontSize: '0.875rem', color: '#6b7280' }}>
                  Remember your password?{' '}
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(false)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--rbf-red)',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      fontSize: 'inherit',
                    }}
                  >
                    Sign In
                  </button>
                </p>
              </>
            )}
          </div>
        </div>
      )}
      {/* Resend Verification Email Modal */}
      {showResendForm && (
        <div className="account-selector-overlay" onClick={() => setShowResendForm(false)}>
          <div className="account-selector-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="account-selector-close"
              onClick={() => setShowResendForm(false)}
              aria-label="Close"
            >
              &times;
            </button>

            {resendFormSuccess ? (
              <div style={{ textAlign: 'center' }}>
                <div className="verification-icon">&#10003;</div>
                <h2 className="account-selector-title">Check Your Email</h2>
                <p className="account-selector-subtitle">
                  If an account exists with <strong>{resendFormEmail}</strong> that needs verification, we've sent a new verification email.
                </p>
                <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1.5rem' }}>
                  Check your spam folder if you don't see it within a few minutes.
                </p>
                <button
                  className="login-button"
                  onClick={() => setShowResendForm(false)}
                >
                  Back to Sign In
                </button>
              </div>
            ) : (
              <>
                <h2 className="account-selector-title">Resend Verification Email</h2>
                <p className="account-selector-subtitle">
                  Enter your email address and we'll send a new verification link.
                </p>

                <form onSubmit={handleResendFormSubmit}>
                  {resendFormError && (
                    <div className="login-error">{resendFormError}</div>
                  )}

                  <div className="form-group">
                    <label htmlFor="resendEmail">Email Address</label>
                    <input
                      type="email"
                      id="resendEmail"
                      value={resendFormEmail}
                      onChange={(e) => setResendFormEmail(e.target.value)}
                      placeholder="your.email@example.com"
                      required
                      disabled={resendFormLoading}
                      autoFocus
                    />
                  </div>

                  <button
                    type="submit"
                    className="login-button"
                    disabled={resendFormLoading}
                    style={{ marginTop: '0.5rem' }}
                  >
                    {resendFormLoading ? 'Sending...' : 'Resend Verification Email'}
                  </button>
                </form>

                <p style={{ textAlign: 'center', marginTop: '1rem', fontSize: '0.875rem', color: '#6b7280' }}>
                  Already verified?{' '}
                  <button
                    type="button"
                    onClick={() => setShowResendForm(false)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--rbf-red)',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      fontSize: 'inherit',
                    }}
                  >
                    Sign In
                  </button>
                </p>
              </>
            )}
          </div>
        </div>
      )}
      <InstallAppBanner />
    </div>
  );
};

export default BridgeClubLogin;
